import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { LoadingScreen } from '../components/LoadingScreen';
import { Pagination } from '../components/Pagination';
import { useAuth } from '../context/AuthContext';
import { getRoleDisplayName } from '../utils/rolePermissions';
import { AlertService } from '../services/AlertService';
import { ProcessingDetailsPanel } from '../components/ProcessingDetailsPanel';
import { TransactionFilters } from '../components/TransactionFilters';
import { DocumentUploadButton } from '../components/DocumentUploadButton';
import { DocumentDialog } from '../components/DocumentDialog';
import { AssignmentAPI, TransactionAPI } from '../services/api';
import { UnifiedTransaction } from '../types/payment';
import { useTransactionFilters, toBackendFilters } from '../hooks/useTransactionFilters';

interface ProcessorAgent {
  id: string;
  username: string;
  full_name: string | null;
  email: string;
  role: string;
  is_active: boolean;
  current_load?: number;
  max_capacity?: number;
  last_assignment?: string;
  two_factor_enabled?: boolean;
  created_at?: string;
  last_login?: string | null;
}

export const PendingProcessing: React.FC = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const highlight = searchParams.get('highlight');
  const [transactions, setTransactions] = useState<UnifiedTransaction[]>([]);
  const [workloadCounts, setWorkloadCounts] = useState<Record<string, number>>({});
  const [totalItems, setTotalItems] = useState(0);
  const [agents, setAgents] = useState<ProcessorAgent[]>([]);
  const [selectedTransaction, setSelectedTransaction] = useState<UnifiedTransaction | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [decryptedData, setDecryptedData] = useState<any>(null);
  const [showEncryptedData, setShowEncryptedData] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [documentDialogRecordId, setDocumentDialogRecordId] = useState<string | null>(null);
  // Headline counts over the full dataset (server aggregate). The fetched
  // arrays are capped at 200, so counting them undercounts once the queue grows.
  const [procStats, setProcStats] = useState<{ pending_assignment: number; total_workload: number }>({ pending_assignment: 0, total_workload: 0 });

  // Check if user has permission to assign transactions
  const canAssignTransactions = !user?.view_only && user?.role && ['processor_team_lead', 'floor_manager', 'director', 'admin'].includes(user.role);

  // Initialize filters (server-side: only the values are used; filtering happens on the backend)
  const {
    filters,
    updateFilter,
    clearFilters,
    hasActiveFilters,
  } = useTransactionFilters(transactions);

  // Server-side pagination: the "showing X of Y" reflects the full filtered set.
  const filterStats = { total: totalItems, filtered: totalItems, hidden: 0 };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Agents + per-agent workload counts + headline stats (loaded once, refreshed on assign).
  const loadStatic = async () => {
    if (!canAssignTransactions) return;
    try {
      const [processingAgents, counts, overviewStats] = await Promise.all([
        AssignmentAPI.getAvailableProcessorAgents(),
        AssignmentAPI.getWorkloadCounts(),
        AssignmentAPI.getProcessingOverviewStats(),
      ]);
      setAgents(processingAgents);
      setWorkloadCounts(counts);
      setProcStats(overviewStats);
    } catch (error) {
      console.error('Failed to load processing agents/stats:', error);
    }
  };

  // The pending table — one server page at a time, filtered + newest-first.
  const loadPending = async () => {
    if (!canAssignTransactions) return;
    try {
      setLoading(true);
      const res = await AssignmentAPI.getPendingAssignmentsPaged(
        (currentPage - 1) * itemsPerPage,
        itemsPerPage,
        toBackendFilters(filters),
      );
      setTransactions(res.items as UnifiedTransaction[]);
      setTotalItems(res.total);
    } catch (error: any) {
      console.error('Failed to load pending assignments:', error);
      AlertService.error('Failed to load assignment data');
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => { await Promise.all([loadPending(), loadStatic()]); };

  // Load agents/stats once when access is confirmed.
  useEffect(() => { loadStatic(); /* eslint-disable-next-line */ }, [canAssignTransactions]);

  // (Re)load the current page whenever paging or filters change.
  useEffect(() => { loadPending(); /* eslint-disable-next-line */ }, [canAssignTransactions, currentPage, itemsPerPage, filters]);

  // Jump back to page 1 when filters change.
  useEffect(() => { setCurrentPage(1); }, [filters]);

  // Auto-open details panel when highlight parameter is present (if on this page).
  useEffect(() => {
    if (highlight && transactions.length > 0 && !loading) {
      const highlightedTransaction = transactions.find(t => t.id === highlight);
      if (highlightedTransaction) {
        setSelectedTransaction(highlightedTransaction);
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, [highlight, transactions, loading]);

  const handleAssignTransaction = async () => {
    if (!selectedTransaction || !selectedAgent) return;

    try {
      setAssigning(true);
      
      // Make actual API call to assign transaction
      await AssignmentAPI.assignTransaction(selectedTransaction.id, selectedAgent);
      
      AlertService.success(`Transaction assigned to ${agents.find(a => a.username === selectedAgent)?.full_name || selectedAgent}`);
      setSelectedTransaction(null);
      setSelectedAgent('');
      setShowAssignmentModal(false);
      
      // Reload data to get fresh state from server after assignment
      await refresh();
    } catch (error: any) {
      console.error('Failed to assign transaction:', error);
      AlertService.error(error.message || 'Failed to assign transaction');
    } finally {
      setAssigning(false);
    }
  };


  // Per-agent active-assignment count comes from the server aggregate
  // (workloadCounts), keyed by the stored assigned_to_processor value.
  const getAgentCurrentLoad = (agent: ProcessorAgent): number => {
    return workloadCounts[agent.username]
      ?? workloadCounts[agent.id]
      ?? workloadCounts[agent.full_name || '']
      ?? 0;
  };

  const getAgentWorkload = (agent: ProcessorAgent): string => {
    const currentLoad = getAgentCurrentLoad(agent);
    const maxCapacity = agent.max_capacity || 10;
    const percentage = (currentLoad / maxCapacity) * 100;
    if (percentage < 50) return 'low';
    if (percentage < 80) return 'medium';
    return 'high';
  };

  const getWorkloadClass = (workload: string): string => {
    switch (workload) {
      case 'low': return 'workload-low';
      case 'medium': return 'workload-medium';
      case 'high': return 'workload-high';
      default: return 'workload-default';
    }
  };

  const handleDecryptInPanel = async (transactionId: string, reason: string) => {
    try {
      const response = await TransactionAPI.decryptTransactionData(transactionId, reason);
      setDecryptedData(response);
      setShowEncryptedData(transactionId);
      AlertService.success('Data decrypted successfully');
    } catch (error: any) {
      console.error('Decryption failed:', error);
      AlertService.error(error.message || 'Failed to decrypt data');
      throw error;
    }
  };

  const handleCloseDetails = () => {
    setSelectedTransaction(null);
    setShowEncryptedData(null);
    setDecryptedData(null);
  };


  // Headline counts come from the server aggregate (full dataset), not the
  // 200-capped fetched arrays.
  const pendingCount = procStats.pending_assignment;
  const totalWorkload = procStats.total_workload;

  if (!canAssignTransactions) {
    return (
      <DashboardLayout>
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>Only Processing Team Leads, Floor Managers, and above can assign transactions.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="pending-processing">
        <div className="page-header">
          <div>
            <h1>Pending Processing</h1>
            <p>Assign approved transactions to processing agents</p>
          </div>
          <div className="header-actions">
            <button className="btn btn-outline-primary" onClick={refresh} disabled={loading}>
              {loading ? 'Loading...' : '🔄 Refresh'}
            </button>
          </div>
        </div>

        {/* Statistics */}
        <div className="stats-grid">
          <div className="stat-card">
            <h3>{pendingCount}</h3>
            <p>Pending Assignment</p>
          </div>
          <div className="stat-card">
            <h3>{agents.filter(a => a.is_active).length}</h3>
            <p>Active Agents</p>
          </div>
          <div className="stat-card">
            <h3>{totalWorkload}</h3>
            <p>Total Workload</p>
          </div>
        </div>

        {/* Agents Overview */}
        <div className="agents-section">
          <h2>Processing Agents</h2>
          <div className="agents-grid">
            {agents.map(agent => {
              const workload = getAgentWorkload(agent);
              const currentLoad = getAgentCurrentLoad(agent);
              const maxCapacity = agent.max_capacity || 10;
              const percentage = (currentLoad / maxCapacity) * 100;
              
              return (
                <div key={agent.id} className={`agent-card ${!agent.is_active ? 'inactive' : ''}`}>
                  <div className="agent-header">
                    <div>
                      <h3>{agent.full_name || agent.username}</h3>
                      <span className={`agent-role role-${agent.role.replace('_', '-')}`}>
                        {getRoleDisplayName(agent.role)}
                      </span>
                    </div>
                    <span className={`status-indicator ${agent.is_active ? 'active' : 'inactive'}`}>
                      {agent.is_active ? 'Active' : 'Offline'}
                    </span>
                  </div>
                  
                  <div className="workload-section">
                    <div className="workload-label">
                      <span>Workload</span>
                      <span>{currentLoad} / {maxCapacity}</span>
                    </div>
                    <div className="workload-bar">
                      <div 
                        className={`workload-fill ${getWorkloadClass(workload)}`}
                        style={{ 
                          width: `${percentage}%`
                        }}
                      />
                    </div>
                  </div>
                  
                  {agent.last_assignment && (
                    <p className="last-assignment">
                      Last assigned: {new Date(agent.last_assignment).toLocaleTimeString()}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Filters */}
        {!loading && transactions.length > 0 && (
          <>
            <TransactionFilters
              filters={filters}
              updateFilter={updateFilter}
              clearFilters={clearFilters}
              hasActiveFilters={hasActiveFilters}
              transactions={transactions}
              filterStats={filterStats}
              showVoidRefundFilters={false}
              showProcessingStatusFilter={true}
            />
          </>
        )}

        {/* Transactions Table */}
        <LoadingScreen 
          isVisible={loading} 
          message="Loading Pending Processing Transactions..."
        />
        
        {!loading && (
          <div className="transactions-table">
            <table>
              <thead>
                <tr>
                  <th>Created By</th>
                  <th>Created</th>
                  <th>Customer</th>
                  <th>Amount</th>
                  <th>State</th> 
                  <th>Category</th>
                  <th>Card</th>
                  <th>Approved By</th>
                  <th>Status</th>
                  <th>Assigned To</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map(transaction => {
                  const currentStatus = transaction.processing_status || 'PENDING_ASSIGNMENT';
                  const customerName = `${transaction.first_name || ''} ${transaction.last_name || ''}`.trim() || 'Unknown';
                  
                  return (
                    <tr key={transaction.id}>
                      <td>{transaction.created_by_name || 'Unknown'}</td>
                      <td className="date-cell">{transaction.created_at ? new Date(transaction.created_at).toLocaleString() : '—'}</td>
                      <td>{customerName}</td>
                      <td>${transaction.amount.toFixed(2)}</td>
                      <td>{transaction.sale_state || transaction.state || 'N/A'}</td>
                      <td>{transaction.category || 'N/A'}</td>
                      <td>
                        {transaction.card_brand && transaction.last4 && (
                          <span className="card-info">
                            {transaction.card_brand} •••• {transaction.last4}
                          </span>
                        )}
                        {transaction.three_ds_status === 'Y' && (
                          <span className="badge-3ds">3DS</span>
                        )}
                        {transaction.three_ds_status === 'Y-3RI' && (
                          <span className="badge-3ds">3RI</span>
                        )}
                      </td>
                      <td>{transaction.approved_by_name || '-'}</td>
                      <td>
                        <span className={`status-badge ${currentStatus.toLowerCase()}`}>
                          {currentStatus === 'PENDING_ASSIGNMENT' ? 'Pending' : 'Assigned'}
                        </span>
                      </td>
                      <td>
                        {transaction.assigned_to_processor_name || '-'}
                        {transaction.assigned_at && (
                          <span className="assigned-time">
                            {new Date(transaction.assigned_at).toLocaleTimeString()}
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="action-buttons">
                          {currentStatus === 'PENDING_ASSIGNMENT' && (
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => {
                                setSelectedTransaction(transaction);
                                setShowAssignmentModal(true);
                              }}
                            >
                              Assign
                            </button>
                          )}
                          <DocumentUploadButton
                            recordId={transaction.id}
                            onClick={() => setDocumentDialogRecordId(transaction.id)}
                          />
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => setSelectedTransaction(transaction)}
                            title="View Details"
                          >
                            📋 Details
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              pageSizeOptions={[10, 20, 30, 50, 100]}
            />

            {!loading && transactions.length === 0 && (
              <div className="no-data">
                <p>No transactions found</p>
              </div>
            )}
          </div>
        )}

        {/* Assignment Modal */}
        {selectedTransaction && showAssignmentModal && (
          <div className="modal-overlay" onClick={() => {
            setSelectedTransaction(null);
            setShowAssignmentModal(false);
          }}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <h2>Assign Transaction</h2>
              
              <div className="transaction-summary">
                <p><strong>Transaction:</strong> #{selectedTransaction.id}</p>
                <p><strong>Customer:</strong> {`${selectedTransaction.first_name || ''} ${selectedTransaction.last_name || ''}`.trim() || 'Unknown'}</p>
                <p><strong>Amount:</strong> ${selectedTransaction.amount.toFixed(2)}</p>
                <p><strong>Category:</strong> {selectedTransaction.category || 'N/A'}</p>
                <p><strong>Approved By:</strong> {selectedTransaction.approved_by_name || 'System'}</p>
              </div>

              <div className="form-group">
                <label>Assign to Agent*</label>
                <select 
                  value={selectedAgent}
                  onChange={(e) => setSelectedAgent(e.target.value)}
                >
                  <option value="">Select an agent...</option>
                  {agents
                    .filter(a => a.is_active && (a.current_load || 0) < (a.max_capacity || 10))
                    .map(agent => (
                      <option key={agent.id} value={agent.username}>
                        {agent.full_name || agent.username} ({agent.current_load || 0}/{agent.max_capacity || 10} assigned)
                      </option>
                    ))}
                </select>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-outline-secondary" onClick={() => {
                  setSelectedTransaction(null);
                  setShowAssignmentModal(false);
                }}>
                  Cancel
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={handleAssignTransaction}
                  disabled={!selectedAgent || assigning}
                >
                  {assigning ? 'Assigning...' : 'Assign Transaction'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Details Panel - Always show when transaction is selected and not in assignment modal */}
        {selectedTransaction && !showAssignmentModal && (
          <ProcessingDetailsPanel
            transaction={selectedTransaction}
            isOpen={true}
            onClose={handleCloseDetails}
            decryptedData={showEncryptedData === ((selectedTransaction as any)?.transaction_id || selectedTransaction?.id) ? decryptedData : null}
            onDecryptData={handleDecryptInPanel}
          />
        )}

        {/* Document Dialog */}
        {documentDialogRecordId && (
          <DocumentDialog
            recordId={documentDialogRecordId}
            isOpen={true}
            onClose={() => setDocumentDialogRecordId(null)}
            recordTitle={transactions.find(t => t.id === documentDialogRecordId)?.first_name + ' ' + transactions.find(t => t.id === documentDialogRecordId)?.last_name}
          />
        )}
      </div>

      <style>{`
        .pending-processing {
          max-width: 1400px;
          margin: 0 auto;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
        }

        .page-header h1 {
          margin: 0 0 0.5rem 0;
          color: var(--text-primary);
          font-size: 2rem;
          font-weight: 600;
        }

        .page-header p {
          margin: 0;
          color: var(--text-secondary);
        }

        .header-actions {
          display: flex;
          gap: 1rem;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .stat-card {
          background: var(--bg-card);
          border-radius: 8px;
          padding: 1.5rem;
          text-align: center;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          border: 1px solid var(--border-primary);
        }

        .stat-card h3 {
          margin: 0 0 0.5rem 0;
          font-size: 2rem;
          font-weight: 600;
          color: var(--primary);
        }

        .stat-card p {
          margin: 0;
          color: var(--text-secondary);
          font-weight: 500;
        }

        .agents-section {
          margin-bottom: 2rem;
        }

        .agents-section h2 {
          color: var(--text-primary);
          margin-bottom: 1rem;
          font-size: 1.25rem;
          font-weight: 600;
        }

        .agents-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 1rem;
        }

        .agent-card {
          background: var(--bg-card);
          border-radius: 8px;
          padding: 1rem;
          border: 1px solid var(--border-primary);
          transition: all 0.2s;
        }

        .agent-card:hover {
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }

        .agent-card.inactive {
          opacity: 0.6;
        }

        .agent-header {
          display: flex;
          justify-content: space-between;
          align-items: start;
          margin-bottom: 1rem;
        }

        .agent-header h3 {
          margin: 0 0 0.25rem 0;
          font-size: 1rem;
          font-weight: 600;
          color: var(--text-primary);
        }

        .agent-role {
          font-size: 0.75rem;
          font-weight: 500;
        }

        .role-processor-agent {
          color: var(--role-processor-agent-text);
        }

        .role-processor-team-lead {
          color: var(--role-processor-team-lead-text);
        }

        .role-sales-team-lead {
          color: var(--role-sales-team-lead-text);
        }

        .role-floor-manager {
          color: var(--role-floor-manager-text);
        }

        .role-director {
          color: var(--role-director-text);
        }

        .role-admin {
          color: var(--role-admin-text);
        }

        .role-agent {
          color: var(--role-agent-text);
        }

        .status-indicator {
          padding: 0.25rem 0.5rem;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .status-indicator.active {
          background: var(--success-light);
          color: var(--success);
        }

        .status-indicator.inactive {
          background: var(--danger-light);
          color: var(--danger);
        }

        .workload-section {
          margin-bottom: 0.75rem;
        }

        .workload-label {
          display: flex;
          justify-content: space-between;
          font-size: 0.875rem;
          color: var(--text-secondary);
          margin-bottom: 0.5rem;
        }

        .workload-bar {
          height: 8px;
          background: var(--bg-secondary);
          border-radius: 4px;
          overflow: hidden;
        }

        .workload-fill {
          height: 100%;
          transition: width 0.3s ease;
        }

        .workload-fill.workload-low {
          background: var(--success);
        }

        .workload-fill.workload-medium {
          background: var(--warning);
        }

        .workload-fill.workload-high {
          background: var(--danger);
        }

        .workload-fill.workload-default {
          background: var(--text-muted);
        }

        .last-assignment {
          margin: 0;
          font-size: 0.75rem;
          color: var(--text-secondary);
        }

        .filters {
          display: flex;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .search-input {
          flex: 1;
          padding: 0.75rem;
          border: 1px solid var(--border-primary);
          border-radius: 4px;
          background: var(--bg-card);
          color: var(--text-primary);
        }

        .filters select {
          padding: 0.75rem;
          border: 1px solid var(--border-primary);
          border-radius: 4px;
          background: var(--bg-card);
          color: var(--text-primary);
          min-width: 200px;
        }

        .loading {
          text-align: center;
          padding: 4rem 2rem;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid var(--border-primary);
          border-top-color: var(--primary);
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .transactions-table {
          background: var(--bg-card);
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .transactions-table table {
          width: 100%;
          border-collapse: collapse;
        }

        .transactions-table th,
        .transactions-table td {
          padding: 1rem;
          text-align: left;
          border-bottom: 1px solid var(--border-primary);
        }

        .transactions-table th {
          background: var(--bg-secondary);
          font-weight: 600;
          color: var(--text-primary);
        }

        .transactions-table td {
          color: var(--text-secondary);
        }

        .card-info {
          font-family: monospace;
          font-size: 0.875rem;
        }

        .status-badge {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 500;
          text-transform: uppercase;
        }

        .status-badge.pending_assignment {
          background: var(--warning-light);
          color: var(--warning);
        }

        .status-badge.assigned {
          background: var(--primary-light);
          color: var(--primary);
        }

        .status-badge.in_process {
          background: var(--accent-light);
          color: var(--accent);
        }

        .status-badge.completed {
          background: var(--success-light);
          color: var(--success);
        }

        .status-badge.cancelled {
          background: var(--danger-light);
          color: var(--danger);
        }

        .assigned-time {
          display: block;
          font-size: 0.75rem;
          color: var(--text-secondary);
          margin-top: 0.25rem;
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;
        }

        .btn-sm {
          padding: 0.375rem 0.75rem;
          font-size: 0.875rem;
        }

        .no-data {
          text-align: center;
          padding: 3rem;
          color: var(--text-secondary);
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal {
          background: var(--bg-card);
          border-radius: 8px;
          padding: 2rem;
          width: 90%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal h2 {
          margin: 0 0 1.5rem 0;
          color: var(--text-primary);
        }

        .transaction-summary {
          background: var(--bg-secondary);
          padding: 1rem;
          border-radius: 4px;
          margin-bottom: 1.5rem;
        }

        .transaction-summary p {
          margin: 0 0 0.5rem 0;
          color: var(--text-secondary);
        }

        .transaction-summary p:last-child {
          margin-bottom: 0;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-group label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 500;
          color: var(--text-primary);
        }

        .form-group select {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid var(--border-primary);
          border-radius: 4px;
          background: var(--bg-primary);
          color: var(--text-primary);
        }

        .form-group select:focus {
          outline: none;
          border-color: var(--primary);
        }

        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
          margin-top: 2rem;
        }

        .access-denied {
          text-align: center;
          padding: 4rem 2rem;
          background: var(--bg-card);
          border-radius: 8px;
        }

        .access-denied h2 {
          color: var(--text-primary);
          margin-bottom: 1rem;
        }

        .access-denied p {
          color: var(--text-secondary);
        }

        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            align-items: stretch;
            gap: 1rem;
          }

          .header-actions {
            flex-direction: column;
          }

          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .agents-grid {
            grid-template-columns: 1fr;
          }

          .filters {
            flex-direction: column;
          }

          .transactions-table {
            overflow-x: auto;
          }

          .action-buttons {
            flex-direction: column;
          }
        }

        .badge-3ds {
          display: inline-block;
          padding: 0.125rem 0.4rem;
          border-radius: 4px;
          font-size: 0.65rem;
          font-weight: 700;
          background: rgba(16, 185, 129, 0.15);
          color: #059669;
          border: 1px solid rgba(16, 185, 129, 0.3);
          margin-left: 0.25rem;
          vertical-align: middle;
        }
      `}</style>
    </DashboardLayout>
  );
};